import {
  ECourierStatus,
  EDealsSortByOptions,
  IDealListAppraisal,
  IDealsScreenFilterOption,
  IFileUploadData,
  IGradePopoverItem,
  IGradeReviewScreenFilterOptions,
  IGradeStatus,
  TSortDirection,
} from '@northladder/services';
import { TTextLocalized } from '@northladder/utilities';

import { TPaginationOptions } from '../../../types';

/**
 * `IFetchAllTopBrands` is type which defines toBrand result of topBrand API.
 */
export interface IFetchAllTopBrands {
  status: boolean;
  data: TopBrand[];
  message: string;
}
/**
 * `TopBrand` is type which defines toBrand item object of topBrand API.
 */
export interface TopBrand {
  brandName?: string;
  categoryName?: string;
  noOfDevices?: number;
  purchasePrice?: number;
  percentage?: number;
  name?: string;
}

/**
 * `ITopModelsPayload` is type which defines the TopModels payload.
 *  fromDate and toDate indicate the dates range to fetch top models from.
 */
export interface ITopModelsPayload {
  fromDate: string;
  toDate: string;
  /**
   * `by` takes 'me'- to fetch my top models and
   * 'nl'- to fetch NorthLadders top models.
   */
  by: 'me' | 'nl';
}

/**
 * `ITopModelsData` is the type for the data received from the Top Models response.
 */
export interface ITopModelsResponseDataRaw {
  groupName: string;
  brandName: string;
  categoryName: string;
  noOfDevices: number;
  purchasePrice: number;
  percentage: number;
}

/**
 * `ITopModelsResponse` is response type for TopModels.
 */
export interface ITopModelsResponse {
  status: boolean;
  message: string;
  data: ITopModelsResponseDataRaw[];
}

export interface ITopModelsResponseData {
  title: string;
  desc: string;
}

/**
 * `IFetchAllTopBrands` is type which defines summary result of
 * inventory summary API.
 */
export interface IFetchSummary {
  status: boolean;
  data: TSummary;
  message: string;
}

/**
 * `ISummary` is type which defines Summary data object of
 * inventory summary API.
 */
export type TSummary = {
  dealsWon: number;
  pendingDelivery: number;
  delivered: number;
  paid: number;
  paymentDue: number;
};

/**
 * This defines the payload for `fetch summary` api.
 */
export interface IFetchSummaryPayload {
  fromDate: string;
  toDate: string;
  by: string;
}

/**
 * Defines the response type for the appraisal list within `IDealsListResponseData`.
 */
export interface IDealListAppraisalRaw {
  appraisalCode: string;
  approvedDeal: {
    approvedAmount: number;
    memberCondition: {
      condition: string;
      dealerCondition: string;
    };
  };
  assetInfo: {
    label: string;
    label_ar: string;
  };
  categoryName: string;
  courierStatus: ECourierStatus;
  dealTS: number;
  deliveryDate: number;
  displayStatus: string[];
  isDelivered: boolean;
  isSellAsset: boolean;
  sellAmount: {
    sellAmount: number;
  };
  serialNumber: string;
}

/**
 * Defines the interface for raw filter options on `DealsListScreen`
 */
interface IDealsFilterOptionRaw {
  label: string;
  label_ar: string;
  value: string;
}

/**
 * `IDealsListResponseData` defines the formatted response of the Deals api data.
 */
export interface IDealsListResponseData {
  appraisals: IDealListAppraisal[];
  count: number;
  filterOptions: IDealsScreenFilterOption[];
}

export interface IFetchTopBrandsPayload {
  by: string;
  fromDate: string;
  toDate: string;
}

/**
 * `IDealsListResponseRawData` defines the raw response of the Deals api data type.
 */
export interface IDealsListResponseRawData {
  appraisals: IDealListAppraisalRaw[];
  count: number;
  filterOptions: IDealsFilterOptionRaw[];
}

/**
 * `IDealsListResponse` defines response type of the deals list api call.
 */
export interface IDealsListResponse {
  data: IDealsListResponseRawData;
  message: string;
  status: boolean;
}

export type TInvoiceType =
  | 'COURIER_FEE'
  | 'DISPUTE_PAY_DIFFERENCE_CREDIT_NOTE'
  | 'DISPUTE_RETURN_COURIER_FEE_CREDIT_NOTE'
  | 'DISPUTE_RETURN_SELL_CREDIT_NOTE'
  | 'SPOT_SALE_TO_UNDERWRITER';

export interface IDealInvoice {
  appraisalCode: string;
  invoiceType: TInvoiceType;
  invoiceUrl: string;
  updatedDate: number;
}

/**
 * Interface for asset images within the deal details API response.
 */
export interface IDealAssetImage {
  orginalImage?: string; // ASK backend guys to update the name to originalImage
  thumbImage: string;
}

/**
 * Interface for appraisal data within the deal details API response.
 */
export interface IDealDetailAppraisalData {
  appraisalCode: string;
  approvedDeal: {
    assetPrice: number;
    assetCondition: {
      memberCondition: {
        condition: string;
        percentValue: string;
        dealerPercentValue: string;
        dealerCondition: string;
      };
    };
  };
  assetImageUrls: IDealAssetImage[];
  awbNumber: string;
  awbTrackingURL: string;
  courierStatus: ECourierStatus;
  dealTS: number;
  deliveryDate: number;
  displayStatus: string[];
  invoices: IDealInvoice[];
  isDelivered: boolean;
  isReturned: boolean;
  paymentType: string;
  serialNumber: string;
  totalAmount: number;
  VATType: string;
}

/**
 * Interface for deal details API response.
 */
export interface IDealDetailsResponseDataRaw {
  appraisalData: IDealDetailAppraisalData;
  assetInfo: {
    name: string;
    name_ar?: string;
    name_de?: string;
    name_es?: string;
    name_fr?: string;
    name_he?: string;
    name_nl?: string;
    name_ro?: string;
    attributes: IAttributes[];
    label: string;
    label_ar?: string;
    label_de?: string;
    label_es?: string;
    label_fr?: string;
    label_he?: string;
    label_nl?: string;
    label_ro?: string;
  };
  brandName: string;
  categoryName: string;
  gradeReviewRequestData: IGradeReviewDetailsResponseRawData[];
  isAutomatedAssessment: boolean;
  outRightSaleInfo: {
    outRightsale: [
      {
        label: string;
        label_ar: string;
        value: string;
      }
    ];
  };
}

export interface IPurchasePrices {
  label: TTextLocalized;
  value: string;
}

/**
 * Interface for attributes within the deal details API response.
 */
export interface IAttributes {
  key: string;
  key_ar?: string;
  key_de?: string;
  key_es?: string;
  key_fr?: string;
  key_he?: string;
  key_nl?: string;
  key_ro?: string;
  value: string;
  value_ar?: string;
  value_de?: string;
  value_es?: string;
  value_fr?: string;
  value_he?: string;
  value_nl?: string;
  value_ro?: string;
}

/**
 * Interface for formatted attributes with localized key and value.
 */
export interface IFormattedAttribute {
  key: TTextLocalized;
  value: TTextLocalized;
}

/**
 * Formatted response of the deal details API response.
 */
export interface IDealDetails {
  appraisalCode: string;
  /**
   * @deprecated - Please use `imagesList`
   */
  assetImageUrls: IDealAssetImage[];
  assetName: TTextLocalized;
  assetNameLabel: string;
  attributes: IAttributes[];
  brandName: string;
  categoryName: string;
  courierStatus: ECourierStatus;
  courierStatusId: ECourierStatus;
  // TODO: Completely remove this later when state.
  // dealerGrade: string; // Removed this to instead use `itemDetailGrade`.
  dealDate: number;
  deliveryDate: number;
  gradeReviewRequestData: IGradeReviewDetailsResponseData[];
  deviceModel: string;
  imagesList: IFileUploadData[];
  imeiNumber: string;
  invoices: IDealInvoice[];
  isAutomatedAssessment: boolean;
  isDelivered: boolean;
  isReturned: boolean;
  itemDetailGrade: string;
  mobileGrade: string;
  myBidOnDealDate: number;
  paymentType: string;
  purchasePrices: IPurchasePrices[];
  priceMyPurchasePrice: number;
  priceCourierFee: number;
  priceCourierFeeIncVAT: number;
  shipmentNumber: string;
  shipmentTrackingUrl: string;
  statusLabels: string[];
  storage: string;
  totalAmount: number;
  vatTypeName: string;
  formattedAttributes: IFormattedAttribute[];
}

export interface IDealDetailsResponse {
  data: IDealDetailsResponseDataRaw;
  message: string;
  status: boolean;
}

// -----------------------------------------------------------------------------
// ---------------------Dealer sign up types------------------------------------
// -----------------------------------------------------------------------------

/**
 * Base Payload for uploading License, VAT certificate in the registration screen.
 */
export type TFileUploadPayloadBase = {
  file: FormData | FormDataEntryValue | null;
  envType: 'WEB' | 'MOBILE';
};

/**
 * Specific type for mobile file upload payload.
 */
export type TFileUploadPayloadMobile = TFileUploadPayloadBase & {
  envType: 'MOBILE';
};

/**
 * Specific type for web file upload payload.
 */
export type TFileUploadPayloadWeb = TFileUploadPayloadBase & {
  path: string;
  type: string;
  envType: 'WEB';
};

export type IFileUploadPayload =
  | TFileUploadPayloadMobile
  | TFileUploadPayloadWeb;

export interface IFileUploadBody {
  fileName: string;
  filePath: string;
  thumbnailPath?: string;
}

/**
 * Fetch appraisals api response type
 */
export interface IFetchAppraisalsResponse {
  data: IFetchAppraisalsData;
  message: string;
  status: boolean;
}

/**
 * `IFetchAppraisalsQueryData` is the response type from react infinite query.
 */
export interface IFetchAppraisalsQueryData {
  pageParams: unknown[];
  pages: IFetchAppraisalsData[];
}

/**
 * Fetch appraisals list response type
 */
export interface IFetchAppraisalsData {
  appraisals: IAppraisalData[];
  count: number;
}

/**
 * Appraisal data type
 *
 * TODO: Why not transform and flatten this if being consumed by components.
 */
export interface IAppraisalData {
  _id: string;
  appraisalCode: string;
  approvedDeal: {
    itemCondition: string;
  };
  assetInfo: {
    label: string;
  };
  assetInventory: {
    sourceStoreName: string;
  };
  dealTS: number;
  sellAmount: {
    dealPrice: number;
    underwriterSellTaxValue: number;
  };
  serialNumber: string;
}

export interface IAppraisalDataRaw {
  approvedDeal: {
    assetCondition: {
      memberCondition: {
        condition: string;
        percentValue: number;
        dealerPercentValue: number;
      };
    };
    assetPrice: string | number;
  };
  customerEmail: string;
  customerName: string;
  customerPhoneNo: string;
  serialNumber: string;
  dealTS: number;
}

export interface IFetchAppraisalDetailsDataRaw {
  appraisalData: IAppraisalDataRaw;
  assetInfo: {
    name: string;
    attributes: {
      key: string;
      value: string;
    }[];
  };
  brandName: string;
  inspectionResults: {
    inspectionName: string;
    condition: string;
  }[];
}

export interface IFetchAppraisalDetailsResponse {
  data: IFetchAppraisalDetailsDataRaw;
  message: string;
  status: boolean;
}

export interface IGetSingedUrlResponse {
  url: string;
}

/**
 * Fetch bid execution time api response type
 */
export interface IBidExecutionTimeResponse {
  data: IBidExecutionData;
  message: string;
  status: boolean;
}

export type TBidExecTime = {
  days: number[];
  time: number;
};

/**
 * IBidExecutionData interface.
 */
export interface IBidExecutionData {
  bidExecTime: TBidExecTime;
  nextExecutionDay: number;
  nextExecutionDayWithTime: number;
  validFrom: number;
  validFromDay: number;
}

/**
 * Credit limit api raw response data
 */
export interface ICreditLimitData {
  availableCredit: number;
  availableCreditPercentage: number;
  blockedCredit: number;
  blockedCreditPercentage: number;
  blockedDeals: any[];
  creditLimit: number;
  effectiveBalance: number;
  isExceeded: boolean;
  isExceededDate: number;
  runningBalance: number;
  runningBalancePercentage: number;
}

/**
 * Credit limit api raw response
 */
export interface ICreditLimitResponse {
  data: ICreditLimitData;
  message: string;
  status: boolean;
}

/**
 * Credit limit api response data convert interface for UI
 */
export interface ICreditLimit {
  availableCredit: number;
  availableCreditPercentage: number;
  blockedCredit: number;
  blockedCreditPercentage: number;
  blockedDealsCount: number;
  creditLimit: number;
  creditLimitExceededDate: number;
  isCreditLimitExceeded: boolean;
  runningBalance: number;
  runningBalancePercentage: number;
}

/**
 * Interface for `IFetchPaginatedAppraisalsPayload` to generate the url with pagination.
 */
export interface IFetchAppraisalsPayload {
  brandId?: string;
  categoryId?: string;
  count: number;
  dealProcessType: string;
  fromDate?: number;
  listType: string;
  search?: string;
  toDate?: number;
  userId?: string;
}

export interface IExportDealsQueryOptions {
  brandId?: string[];
  categoryId?: string;
  courierStatus?: string;
  filterBy?: string[];
  fromDate?: string;
  groupId?: string[];
  paymentType?: string;
  searchKey?: string;
  searchType?: string;
  toDate?: string;
}

/**
 * Interface for `IFetchPaginatedAppraisalsPayload` to generate the url with pagination.
 */
export interface IFetchPaginatedAppraisalsPayload
  extends IFetchAppraisalsPayload,
    TPaginationOptions {}

// /**
//  * Interface for `IFetchPaginatedAppraisalsPayload` to generate the url.
//  * @deprecated Using the string directly.
//  */
// export interface IFetchAppraisalDetailsPayload {
//   appraisalId: string;
// }

export interface DeviceComponentCondition {
  condition: string;
  itemCondition: string;
  itemConditionId: string;
}

export interface IDeviceGradeConditionData {
  _id: string;
  name: string;
  conditions: DeviceComponentCondition[];
}

export interface IDeviceGradeConditionRawResponse {
  status: boolean;
  message: string;
  data: IDeviceGradeConditionData[];
}

export interface IDeviceItemCondition {
  _id: string;
  condition: string;
  condition_ar?: string;
  freeze: boolean;
}

export interface IDeviceItemConditionData {
  _id: string;
  conditions: IDeviceItemCondition[];
}

export interface IDeviceItemConditionRawResponse {
  status: boolean;
  message: string;
  data: IDeviceItemConditionData;
}

/**
 * `IDeviceGradeCondition` defines the formatted response of the Device Item condition api data.
 */
export interface IDeviceItemConditions {
  id: string;
  conditions: IGradePopoverItem[];
}

// TODO: Merge apis/GradeReviewStatus and services/IGradeStatus enum/
export enum EGradeReviewStatus {
  DISPUTE_ADJUSTED = 'DISPUTE_ADJUSTED',
  UNDER_PROCESS = 'UNDER_PROCESS',
  GRADE_REVISED = 'GRADE_REVISED',
  GRADE_UNCHANGED = 'GRADE_UNCHANGED',
  ADDITIONAL_INFO_REQUIRED = 'ADDITIONAL_INFO_REQUIRED',
  REQUESTED_TO_RETURN = 'REQUESTED_TO_RETURN',
  RETURN_IN_PROGRESS = 'RETURN_IN_PROGRESS',
  DEVICE_RETURNED = 'DEVICE_RETURNED',
  NONE = 'NONE',
}

/**
 * Grade Review Request raw response data
 */
export interface IGradeReviewRequestDataRaw {
  _id: string;
  appraisalCode: string;
  assetName: string;
  createdAt: number;
  creditAmount?: number;
  dealPrice: number;
  dealPaymentType: 'COD' | 'CC';
  financeCheck: boolean;
  isAutomatedAssessment: boolean;
  isCreatedByNL: boolean;
  status: IGradeStatus;
  ticketNumber: string;
}

/**
 * Interface for single data item in grade review request list for formatted response.
 */
export interface IGradeReviewList {
  appraisalCode: string;
  assetName: string;
  brandName?: string;
  /**
   * We are using generic name as `date` since we are using this type at other
   * places as well. Date can be changed according to deal status.
   */
  date: number;
  displayStatus: string[];
  gradeCondition?: string;
  gradeReviewRequestId: string;
  gradeReviewTicketNumber: string;
  gradeStatus: IGradeStatus;
  gradeStatusName?: string;
  isAutomatedAssessment: boolean;
  isCreatedByNL: boolean;
  purchasePrice: number;
  revisedPrice?: number;
  serialNumber?: string;
  validateErrorMessages?: string[];
}

/**
 * Interface for raw filter options for grade review request list.
 */
export interface IGradeFilterOptionsRaw {
  count: number;
  key: keyof typeof IGradeStatus;
  label: {
    critical: boolean;
    value: {
      en: string;
      ar: string;
    };
  };
  value: {
    en: string;
    ar: string;
  };
}

/**
 * interface for raw response we get from the API for filters of grade review
 * request list.
 */
export interface IGradeReviewRequestListResponseRawData {
  gradeReviewRequestList: IGradeReviewRequestDataRaw[];
  totalCount: number;
  status: IGradeFilterOptionsRaw[];
}

/**
 * `IDealsListResponse` defines response type of the deals list api call.
 */
export interface IGradeReviewRequestListResponse {
  data: IGradeReviewRequestListResponseRawData;
  message: string;
  status: boolean;
}

/**
 * Interface to format the grade review request list response.
 */
export interface IGradeReviewRequestListResponseData {
  gradeList: IGradeReviewList[];
  count: number;
  gradeFilterOptions: IGradeReviewScreenFilterOptions[];
}

/**
 * Interface of raw data for filter options for grade review request list.
 */
export interface IGradeReviewFilterOptionsRawData {
  key: keyof typeof IGradeStatus;
  value: {
    en: string;
  };
}

/**
 * Interface of raw response we get from the API for filters of grade review request list.
 */
export interface IGradeReviewFilterOptionsRawResponse {
  data: IGradeReviewFilterOptionsRawData[];
  message: string;
  status: boolean;
}

/**
 * Interface for Search Grade Deal list response
 */
export interface IGradeDealsSearchDealRawItem {
  _id: string;
  appraisalCode: string;
  appraisalStatus: string;
  assetId: string;
  brandName: string;
  courierStatus: string;
  dealPrice: number;
  deliveredOn: number;
  label_ar: string;
  label: string;
  paymentType: string;
  saleGrade: string;
  isAutomatedAssessment: boolean;
  serialNumber: string;
  status: string;
  validationErrors: string[];
}

/**
 * Interface for Search Grade Deal list response data
 */
export interface IGradeDealsSearchRawData {
  appraisals: IGradeDealsSearchDealRawItem[];
  totalCount: number;
}

/**
 * Interface for Search Grade Deal list raw response
 */
export interface IGradeDealsSearchRawResponse {
  status: boolean;
  message: string;
  data: IGradeDealsSearchRawData;
}

/**
 * Interface for grade review details dealer evaluation
 */
export interface IGradeReviewDealerEvaluation {
  name: string;
  selectedCondition: string;
}

export interface IValidationMessage {
  critical: boolean;
  value: TTextLocalized;
}

/**
 * Interface for raw data of the grade review request details.
 * TODO: Tell BE to replace the _id to something meaningful.
 */
export interface IGradeReviewDetailsResponseRawData {
  _id: string;
  appraisalCode: string;
  AWBNumber: string;
  awbTrackingURL?: string;
  assetName: string;
  bidAmount: number;
  brandName: string;
  courierBooked: boolean;
  courierStatus: string;
  courierStatusLocale: TTextLocalized;
  createdAt: number;
  dealerActionBefore?: number;
  createdBy: string;
  creditAmount?: number;
  dealPaymentType: string;
  dealerBranchName: string;
  dealerEvaluation: IGradeReviewDealerEvaluation[];
  financeClosingDate?: number;
  deliveredDate: number | null;
  revisedGrade?: string;
  revisedGradePercentage?: number;
  dealPrice: number;
  financeCheck: boolean;
  financeStatus: string;
  imageUrls: IFileUploadData[];
  imeiNumber: string;
  initialSaleGrade: string;
  isAutomatedAssessment: boolean;
  initialSaleGradePercentage: number;
  isCreatedByNL: boolean;
  latestRemark: string;
  comment: string;
  requestedSaleGrade: string;
  requestedSaleGradeId: string;
  requestedSaleGradePercentage: number;
  status: keyof typeof IGradeStatus;
  ticketNumber: string;
  updatedAt?: number;
  validationMessages: IValidationMessage[];
  videoUrls: IFileUploadData[];
  canCancelRequest: boolean;
}

/**
 * Interface for response of the grade review request details.
 */
export interface IGradeReviewDetailsResponse {
  data: IGradeReviewDetailsResponseRawData;
  message: string;
  status: boolean;
}

/**
 * Interface for the formatted response of the grade review request details.
 */
export interface IGradeReviewDetailsResponseData {
  gradeRequestId: string;
  appraisalCode: string;
  AWBNumber: string;
  awbTrackingURL?: string;
  assetName: string;
  bidPrice: number;
  branchName: string;
  brandName: string;
  isCourierBooked: boolean;
  courierStatus: TTextLocalized;
  createdAt: number;
  dealerActionBefore?: number;
  createdBy: string;
  creditAmount?: number;
  deliveredDate: number | null;
  displayStatus: string[];
  dealPaymentType: string;
  financeClosingDate?: number;
  financeStatus: string;
  gradeReviewRequestId: string;
  gradeStatus: string;
  gradeStatusEnum: EGradeReviewStatus;
  imageList: IFileUploadData[];
  imeiNumber: string;
  initialSaleGrade: string;
  initialSaleGradePercentage: number;
  isAutomatedAssessment: boolean;
  isCreatedByNL: boolean;
  latestRemark: string;
  comment: string;
  proposedGradeReasonList: IGradeReviewDealerEvaluation[];
  purchasePrice: number;
  requestedSaleGrade: string;
  requestedSaleGradeId: string;
  requestedSaleGradePercentage: number;
  revisedGrade?: string;
  revisedGradePercentage?: number;
  ticketId: string;
  updatedAt?: number;
  validationMessages: IValidationMessage[];
  videoList: IFileUploadData[];
  canCancelRequest: boolean;
}

/**
 * Interface for the raw data of the grade review request history.
 */
export interface IGradeReviewHistoryResponseRawData
  extends IGradeReviewDetailsResponseRawData {
  gradeReviewRequestId: string;
}

export type TGradeReviewHistoryResponseRawData =
  IGradeReviewHistoryResponseRawData[];

/**
 * Interface for the raw response of the grade review request history.
 */
export interface IGradeReviewHistoryResponse {
  data: IGradeReviewHistoryResponseRawData[];
  message: string;
  status: boolean;
}

export interface IGradeReviewHistoryResponseData
  extends Omit<
    IGradeReviewDetailsResponseData,
    'canCancelRequest' | 'gradeStatusEnum' | 'validationMessages'
  > {
  gradeReviewRequestId: string;
}

/**
 * Interface for the formatted response of the grade review request history.
 */
export type TGradeReviewHistoryResponseData = {
  gradeReviewHistoryList: IGradeReviewHistoryResponseData[];
};

/**
 * Interface for the create grade change request response data.
 */
export interface ICreateGradeChangeRequestData {
  gradeReviewRequestId: string;
}

/**
 * Interface for the create grade change request response.
 */
export interface ICreateGradeChangeRequestResponse {
  status: boolean;
  message: string;
  data: ICreateGradeChangeRequestData;
}

/**
 * Interface for the update grade change request response data.
 */
export interface IUpdateGradeChangeRequestData {
  ticketNumber: string;
}
/**
 * Interface for the update grade change request response.
 */
export interface IUpdateGradeChangeRequestResponse {
  status: boolean;
  message: string;
  data: IUpdateGradeChangeRequestData;
}

export interface IDealerEvaluation {
  name: string;
  selectedCondition: string;
}

export interface ICreateGradeChangeRequestPayload {
  appraisalCode: string;
  imageUrls: IFileUploadBody[];
  videoUrls: IFileUploadBody[];
  requestedSaleGradeId: string;
  requestedSaleGradeSnapShotId: string;
  dealerEvaluation: IDealerEvaluation[];
  latestRemark: string;
}

export interface IUpdateGradeChangeRequestPayload {
  event: string;
  gradeReviewRequestId: string;
  cancelTicket: boolean;
  imageUrls?: IFileUploadBody[];
  videoUrls?: IFileUploadBody[];
  requestedSaleGradeId?: string;
  requestedSaleGradeSnapShotId?: string;
  dealerEvaluation?: IDealerEvaluation[];
  latestRemark?: string;
}

// ----- The following types have to be revised.......

/**
 * Interface for `TDownloadUploadBidFilePayload` to generate the url with categoryId.
 */
export type TDownloadUploadBidFilePayload = {
  categoryId: string;
};

// /**
//  * Interface for getSignedUrl props.
// @deprecated since we pass the fileName directly, delete this later.
//  */
// export interface IGetSignedUrlPayload {
//   fileName: string;
// }

/**
 * Interface for getting bid details api's payload
 */
export interface IFetchBidDetailsParams {
  assetId: string;
  onSuccess?: (stepperValue: number) => void;
}

/**
 * Interface for `DealListUrl` to generate the deals list url with appraisal code,
 * and pagination support.
 */
export interface IFetchDealsUrlOptions {
  brandIds?: string[];
  categoryId?: string;
  count: number;
  field: string;
  filters?: string;
  fromDate?: string | null;
  groupIds?: string[];
  orderBy?: TSortDirection | null;
  page: number;
  searchText?: string;
  sortBy?: EDealsSortByOptions | null;
  toDate?: string | null;
}

export interface ITermsAndConditionParams {
  /**
   * Selected language by the user
   */
  language: 'en' | 'ar';
  /**
   * User country code from selected region
   */
  countryCode: string;
}

/**
 * Bid Details api param
 */
export interface IFetchBidDetailsParam {
  assetId: string;
}

/**
 * Interface for fetching the grade review url.
 */
export interface IFetchGradeReviewUrlOptions {
  appraisalCode?: string;
  count: number;
  fromDate?: string | null;
  page: number;
  status?: string;
  toDate?: string | null;
}

/**
 * Interface for `DeviceGradeConditionUrl` to generate the url with store.
 */
export interface IFetchDeviceGradeConditionUrlOptions {
  appraisalCode: string;
}

/**
 * Interface for fetching the grade review url.
 */
export interface IFetchGradeDealsUrlOptions {
  appraisalCode?: string;
  count: number;
  page: number;
}
